import slugify from 'slugify';

import { mapApiImageToImageProps } from './common.mappers';

import type {
  ContentItem,
  FBlogPostTemplateProps,
} from '@/components/templates/FBlogPostTemplate.vue';

const slugifyOptions = { lower: true, remove: /[*+~.()'"!:@?]/g };

export function mapApiDataToBlogPost(
  blogPostRawData: StrapiResponseData<BlogPostContentType> | undefined
): FBlogPostTemplateProps | null {
  const blogPostData = blogPostRawData?.attributes;
  if (!blogPostData) return null;

  return {
    title: blogPostData.title,
    slug: blogPostData.slug,
    excerpt: blogPostData.excerpt,
    publishedDate:
      blogPostData.published_at_override || blogPostData.publishedAt,
    author: blogPostData.author_override ?? 'Fifteen',
    mainAudience: blogPostData.audiences?.data?.[0]?.attributes?.name,
    image: mapApiImageToImageProps(
      blogPostData.main_image?.data?.attributes,
      'large'
    ),
    relatedPosts: (blogPostData.related_posts || []).map(post => ({
      text: post.title ?? '',
      image: mapApiImageToImageProps(post.main_image),
      link: { location: `/resources/blog/${post.slug}` },
      linkText: '',
    })),
    contentItems: (blogPostData.content || []).map(
      mapBlogPostContentDataToContentProps
    ),
  };
}

function mapBlogPostContentDataToContentProps(
  contentItem: BlogPostContent
): ContentItem {
  switch (contentItem.__component) {
    case 'blog.button':
      return mapBlogButtonToProps(contentItem);
    case 'blog.cta':
      return mapBlogCtaToProps(contentItem);
    case 'blog.image':
      return mapBlogImageToProps(contentItem);
    case 'blog.product-card':
      return mapBlogProductCardToProps(contentItem);
    case 'blog.read-also':
      return mapBlogReadAlsoToProps(contentItem);
    case 'blog.text-content':
      return mapBlogTextContentDataToProps(contentItem);
    case 'blog.highlight':
      return mapBlogHighlightToProps(contentItem);
    case 'blog.blockquote':
      return mapBlogBlockquoteToProps(contentItem);
  }
}

export interface BlogContentItem<T> {
  type: BlogPostContent['__component'];
  props: T;
}

function mapBlogButtonToProps(blogButton: BlogButton): ContentItem {
  return {
    type: 'blog.button',
    props: {
      location: blogButton.link ?? '',
      text: blogButton.text ?? '',
    },
  };
}

function mapBlogCtaToProps(blogCta: BlogCta): ContentItem {
  return {
    type: 'blog.cta',
    props: {
      buttonText: blogCta.button_text ?? '',
      text: blogCta.text ?? '',
      url: blogCta.button_link ?? '',
      color: blogCta.color ?? 'primary',
    },
  };
}

function mapBlogImageToProps(blogImage: BlogImage): ContentItem {
  const image = mapApiImageToImageProps(
    blogImage?.image?.data?.attributes,
    'medium'
  );
  return {
    type: 'blog.image',
    props: {
      image,
      width: image.width,
      caption: blogImage.caption,
      captionLink: {
        text: blogImage.caption_link_text ?? '',
        link: blogImage.caption_link_link ?? '',
      },
      link: blogImage.image_link ?? '',
      showPlayerIcon: blogImage.show_player_icon ?? false,
    },
  };
}

function mapBlogProductCardToProps(
  blogProductCard: BlogProductCard
): ContentItem {
  return {
    type: 'blog.product-card',
    props: {
      type: blogProductCard.type ?? 'fusion',
      buttonLocation: blogProductCard.link ?? '',
    },
  };
}

function mapBlogReadAlsoToProps(blogReadAlso: BlogReadAlso): ContentItem {
  return {
    type: 'blog.read-also',
    props: {
      text: blogReadAlso.blog_post?.data?.attributes?.title ?? '',
      link: {
        location: `/resources/blog/${
          blogReadAlso.blog_post?.data?.attributes?.slug ?? ''
        }`,
      },
    },
  };
}

function mapBlogTextContentDataToProps(
  blogTextContent: BlogTextContent
): ContentItem {
  return {
    type: 'blog.text-content',
    props: {
      source:
        blogTextContent.content?.replace(
          /<h2.*?>(.*?)<\/h2>/g,
          (_, p1) =>
            `<h2 id="${slugify(
              // Roughly remove any inner HTML tag
              p1.replace(/(<\s*[a-z]+[^>]*>|<\s*\/\s*[a-z]+\s*>)/g, ' '),
              slugifyOptions
            )}">${p1}</h2>`
        ) ?? '',
    },
  };
}

function mapBlogBlockquoteToProps(blogBlockquote: BlogBlockquote): ContentItem {
  return {
    type: 'blog.blockquote',
    props: {
      text: blogBlockquote.quote ?? '',
      color: blogBlockquote.color ?? 'primary',
      author: blogBlockquote.author ?? '',
      authorTitle: blogBlockquote.author_title ?? '',
    },
  };
}

function mapBlogHighlightToProps(blogHighlight: BlogHighlight): ContentItem {
  return {
    type: 'blog.highlight',
    props: {
      text: blogHighlight.text ?? '',
    },
  };
}
